import { useEffect, useMemo } from "react";

type SeoProps = {
  title: string;
  description: string;
  keywords?: string;
  canonical?: string;
  type?: string;
  jsonLd?: object | object[];
};

const BASE = "https://bknos.cn";

function setMeta(attr: "name" | "property", key: string, content: string) {
  let el = document.head.querySelector(
    `meta[${attr}="${key}"]`
  ) as HTMLMetaElement | null;
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setLink(rel: string, href: string) {
  let el = document.head.querySelector(
    `link[rel="${rel}"]`
  ) as HTMLLinkElement | null;
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

export default function Seo({
  title,
  description,
  keywords,
  canonical,
  type = "website",
  jsonLd,
}: SeoProps) {
  const jsonLdString = useMemo(
    () => (jsonLd ? JSON.stringify(jsonLd) : ""),
    [jsonLd]
  );

  useEffect(() => {
    const fullTitle = title ? `${title} | 吃瓜视频网` : "吃瓜视频网";
    document.title = fullTitle;
    setMeta("name", "description", description);
    if (keywords) setMeta("name", "keywords", keywords);
    const url = canonical || (typeof window !== "undefined" ? window.location.href : BASE);
    setLink("canonical", url);
    setMeta("property", "og:title", fullTitle);
    setMeta("property", "og:description", description);
    setMeta("property", "og:type", type);
    setMeta("property", "og:url", url);
    setMeta("property", "og:site_name", "吃瓜视频网");
    setMeta("property", "og:locale", "zh_CN");
    setMeta("name", "twitter:card", "summary_large_image");
    setMeta("name", "twitter:title", fullTitle);
    setMeta("name", "twitter:description", description);

    document.getElementById("page-jsonld")?.remove();
    if (jsonLdString) {
      const s = document.createElement("script");
      s.type = "application/ld+json";
      s.id = "page-jsonld";
      s.textContent = jsonLdString;
      document.head.appendChild(s);
    }
    return () => {
      document.getElementById("page-jsonld")?.remove();
    };
  }, [title, description, keywords, canonical, type, jsonLdString]);

  return null;
}